<template>
  <div class="kl-login-page">
    <template v-if="$maintenance_view">
      <MaintenanceView></MaintenanceView>
    </template>
    <div v-else class="container-login">
      <div class="login-box">
        <div class="login-box__header">
          <h2 class="login-box__title">{{ $t("reusable.login.login") }}</h2>
        </div>
        <form @submit.prevent="authenticate">
          <b-form-group
            id="input-group-user"
            :label="$t('reusable.login.email')"
            label-for="input-user"
          >
            <b-form-input
              id="input-user"
              type="email"
              required
              :placeholder="$t('reusable.login.email')"
              v-model="$v.user_form.email.$model"
              autocomplete="email"
              :state="validateState('email')"
              aria-describedby="input-user-name-feedback"
              @input="
                $v.user_form.email.$model =
                  $v.user_form.email.$model.toLowerCase()
              "
            ></b-form-input>
            <!-- @keyup.enter="authenticate" -->
            <b-form-invalid-feedback id="input-user-name-feedback">{{
              $t("reusable.common.required")
            }}</b-form-invalid-feedback>
          </b-form-group>
          <b-form-group
            id="input-group-password"
            :label="$t('reusable.login.password')"
            label-for="input-password"
          >
            <div class="d-flex login-password">
              <b-form-input
                id="input-password"
                class="m-0"
                v-model="user_form.password"
                :type="show_password ? 'text' : 'password'"
                required
                :placeholder="$t('reusable.login.password')"
                autocomplete="current-password"
              ></b-form-input>
              <b-input-group-append>
                <ButtonSmall
                  :custom_class="'p-2'"
                  :variant="'secondary'"
                  :tooltip_text="
                    !show_password
                      ? $t('reusable.login.showPassword')
                      : $t('reusable.login.hidePassword')
                  "
                  :click_button="() => (show_password = !show_password)"
                  :icon="show_password ? 'eye' : 'eye-slash'"
                ></ButtonSmall>
              </b-input-group-append>
            </div>
            <!-- @keyup.enter="authenticate" -->
          </b-form-group>
          <div class="forgot-password" @click="launchRecoverPasswordModal()">
            <!-- @click="$bvModal.show('modal-recovery-password')" -->
            {{ $t("reusable.login.forgotPassword") }}
          </div>
          <!-- <b-button @click="authenticate" variant="primary">Entrar</b-button> -->
          <b-button type="submit" variant="primary">{{
            $t("reusable.login.enter")
          }}</b-button>
        </form>
        <!-- SSO Divider -->
        <div
          v-if="
            (login_institutions &&
              login_institutions.length > 0 &&
              is_klearning_main_domain) ||
            hasProvider('azure') ||
            hasProvider('google')
          "
          class="sso-divider"
        >
          <span>{{ $t("reusable.login.or") }}</span>
        </div>

        <!-- SSO Login Section -->
        <div class="sso-section">
          <b-button
            v-if="hasProvider('azure')"
            variant="outline-primary"
            class="sso-button w-100 mb-2"
            @click="showSSOInstitutionModal('azure')"
          >
            <img :src="microsoft_logo" alt="Microsoft" class="microsoft-icon" />
            {{ $t("reusable.login.signInWithMicrosoft") }}
          </b-button>
          <b-button
            v-if="hasProvider('google')"
            variant="outline-primary"
            class="sso-button w-100"
            @click="showSSOInstitutionModal('google')"
          >
            <img :src="google_logo" alt="Google" class="microsoft-icon" />
            {{ $t("reusable.login.signInWithGoogle") }}
          </b-button>
        </div>

        <template v-if="login_institutions && login_institutions.length > 0">
          <!-- Institution redirect section (only shown on klearning.cl main domain) -->
          <template v-if="is_klearning_main_domain">
            <div class="sso-divider">
              <span>{{ $t("reusable.login.selectYourInstitution") }}</span>
            </div>
            <div class="institutions-grid">
              <div
                v-for="institution in login_institutions"
                :key="institution.id"
                class="institution-card"
                @click="redirectToInstitution(institution)"
                :title="institution.name"
              >
                <div class="institution-icon">
                  <img
                    v-if="institution.logo_url"
                    :src="institution.logo_url"
                    :alt="institution.name"
                    class="institution-logo"
                  />
                  <b-icon v-else icon="building" font-scale="1.5"></b-icon>
                </div>
                <!-- <span class="institution-name">{{ institution.name }}</span> -->
              </div>
            </div>
          </template>
        </template>
      </div>
    </div>
  </div>
</template>


<script>
import { validationMixin } from "vuelidate";
import { required, email } from "vuelidate/lib/validators";
import { mapGetters } from "vuex";
import SSOService from "@/services/sso.service";
import { isGenericKlearningHost, buildSubdomainUrl } from "@/utils/utils";

export default {
  name: "Login",
  components: {
    ButtonSmall: () =>
      import("@/components/reusable/Buttons/Small/ButtonSmall"),
    MaintenanceView: () => import("@/components/public/MaintenanceView"),
  },
  mixins: [validationMixin],
  data() {
    return {
      user_form: {
        email: "",
        password: "",
      },
      recovery_password_email: "",
      user: null,
      show_password: false,
      sso_institutions: [],
      login_institutions: [],
      loading_sso: false,
      auto_sso_attempted: false, // Flag para evitar loops infinitos
      microsoft_logo: require("@/assets/microsoft_logo.svg"),
      google_logo: require("@/assets/google_logo.svg"),
    };
  },
  validations: {
    user_form: {
      email: {
        required,
      },
      password: {
        required,
      },
    },
    recovery_password_email: {
      email,
    },
  },
  computed: {
    ...mapGetters({ isLoged: "isLoged" }),
    is_klearning_main_domain() {
      return isGenericKlearningHost(window.location.hostname);
    },
  },
  methods: {
    validateState(key) {
      const { $dirty, $error } = this.$v.user_form[key];
      return $dirty ? !$error : null;
    },
    validateRecoveryPassword() {
      const { $dirty, $error } = this.$v.recovery_password_email;
      return $dirty ? !$error : null;
    },
    launchRecoverPasswordModal() {
      // #TODO: Tomar el correo si está disponible y rellenar el input.
      // Será mejor crear un formulario para colocarlo dentro del modal.
      this.$swal
        .fire({
          title: this.$t("reusable.login.enterEmailToFindAccount"),
          input: "email",
          confirmButtonText: this.$t("reusable.login.send"),
          inputValue: this.user_form.email,
          reverseButtons: true,
          showLoaderOnConfirm: false,
          showCancelButton: true,
          preConfirm: (login) => {
            this.$swal.fire({
              title: this.$t("reusable.common.loading"),
              onBeforeOpen: () => {
                this.$swal.showLoading();
              },
            });
            return this.$restful
              .Get(`/authentication/send_reset_code/?email=${login}`)
              .then(() => {
                return { isConfirmed: true };
              })
              .catch((error) => {
                this.$swal.showValidationMessage(`Request failed: ${error}`);
              });
          },
        })
        .then((result) => {
          if (result && result.value && result.value.isConfirmed) {
            this.$swal.fire({
              type: "success",
              title: this.$t("reusable.login.checkYourEmail"),
              showConfirmButton: false,
              timer: 2500,
            });
          }
        });
    },
    authenticate() {
      this.$v.user_form.$touch();
      if (this.$v.user_form.$anyError) {
        return;
      }

      // Si el usuario usa login tradicional, marcar que no quiere auto-login
      this.auto_sso_attempted = true;
      localStorage.setItem(
        "sso_auto_login_attempted",
        window.location.hostname
      );

      // TODO: Se oculta seccion de Idioma hasta que este finalizado
      // Forzar idioma a español al hacer login
      // this.$i18n.locale = "es";
      // localStorage.setItem("user_language", "es");

      this.$store.dispatch("login", {
        email: this.user_form.email,
        password: this.user_form.password,
      });
    },
    async loadLoginInstitutions() {
      try {
        const response = await this.$restful.Post(
          "/authentication/login-institutions/",
          {}
        );
        this.login_institutions = response.institutions || [];
      } catch (error) {
        console.error("Error loading login institutions:", error);
        this.login_institutions = [];
      }
    },
    hasProvider(name) {
      // Dominio principal (dev/producción/localhost): mostrar si cualquier institución tiene el provider
      if (this.is_klearning_main_domain) {
        return this.sso_institutions.some(
          (inst) => inst.providers && inst.providers.includes(name)
        );
      }

      // Subdominio de institución: mostrar solo si la institución que coincide con la URL tiene el provider
      const hostname = window.location.hostname;
      if (hostname.includes("klearning.cl")) {
        const parts = hostname.split(".");
        if (parts.length >= 3) {
          const urlSubdomain = parts[0].replace(/-dev$/, "");
          const matchedInstitution = this.sso_institutions.find((inst) => {
            if (!inst.subdomain) return false;
            return urlSubdomain.toLowerCase() === inst.subdomain.toLowerCase();
          });
          return !!(
            matchedInstitution &&
            matchedInstitution.providers &&
            matchedInstitution.providers.includes(name)
          );
        }
      }

      // Fallback: mostrar si cualquier institución tiene el provider
      return this.sso_institutions.some(
        (inst) => inst.providers && inst.providers.includes(name)
      );
    },
    async loadSSOInstitutions() {
      try {
        this.loading_sso = true;
        this.sso_institutions = await SSOService.getInstitutionsWithSSO();

        // DATA DE PRUEBA: DESCOMENTAR PARA REALIZAR PRUEBAS
        // DEV ONLY: inject mock institutions if backend returns empty
        // if (
        //   process.env.NODE_ENV === "development" &&
        //   this.sso_institutions.length === 0
        // ) {
        //   this.sso_institutions = [
        //     {
        //       id: 1,
        //       name: "Universidad Finis Terrae",
        //       value: 1,
        //       domain: "finisterrae.cl",
        //       logo_url: require("@/assets/new-page/Logo_Finis_Terrae.png"),
        //     },
        //     {
        //       id: 2,
        //       name: "Duoc UC",
        //       value: 2,
        //       domain: "duoc.cl",
        //       logo_url: require("@/assets/new-page/Logo_Duoc.png"),
        //     },
        //     {
        //       id: 3,
        //       name: "IPSS",
        //       value: 3,
        //       domain: "ipss.cl",
        //       logo_url: require("@/assets/new-page/Logo_IPSS.png"),
        //     },
        //     {
        //       id: 4,
        //       name: "Universidad de Alba",
        //       value: 4,
        //       domain: "udalba.cl",
        //       logo_url: require("@/assets/new-page/Logo_Udalba.png"),
        //     },
        //     {
        //       id: 5,
        //       name: "ENAC",
        //       value: 5,
        //       domain: "enac.cl",
        //       logo_url: require("@/assets/new-page/Logo_ENAC.png"),
        //     },
        //     {
        //       id: 6,
        //       name: "IACC",
        //       value: 6,
        //       domain: "iacc.cl",
        //       logo_url: require("@/assets/new-page/Logo_IACC.png"),
        //     },
        //     {
        //       id: 7,
        //       name: "DRU",
        //       value: 7,
        //       domain: "dru.cl",
        //       logo_url: require("@/assets/new-page/Logo_DRU.jpg"),
        //     },
        //     {
        //       id: 8,
        //       name: "UNIACC",
        //       value: 7,
        //       domain: "uniacc.cl",
        //       logo_url: require("@/assets/new-page/Logo_UNIACC.png"),
        //     },
        //   ];
        // }

        // Después de cargar instituciones, intentar auto-login si es posible
        await this.attemptAutoSSOLogin();
      } catch (error) {
        console.error("Error loading SSO institutions:", error);
        this.sso_institutions = [];
      } finally {
        this.loading_sso = false;
      }
    },
    async attemptAutoSSOLogin() {
      // Evitar intentos múltiples
      if (this.auto_sso_attempted) {
        return;
      }

      // Marcar como intentado
      this.auto_sso_attempted = true;

      // Verificar que hay instituciones SSO cargadas
      if (this.sso_institutions.length === 0) {
        return;
      }

      // Extraer subdomain del URL
      const hostname = window.location.hostname;
      let urlSubdomain = null;

      // Extraer subdomain de patterns como "ipss-dev.klearning.cl" o "ipss.klearning.cl"
      if (hostname.includes("klearning.cl") && hostname !== "klearning.cl") {
        const parts = hostname.split(".");
        if (parts.length >= 3) {
          // Obtener la primera parte y remover sufijo "-dev" si existe
          urlSubdomain = parts[0].replace(/-dev$/, "");
        }
      }

      // Si no hay subdomain, no hacer nada
      if (!urlSubdomain) {
        return;
      }

      // Buscar institución que coincida con el subdomain
      const matchedInstitution = this.sso_institutions.find((inst) => {
        if (!inst.subdomain) return false;
        return urlSubdomain.toLowerCase() === inst.subdomain.toLowerCase();
      });

      if (!matchedInstitution) return;

      // Si hay coincidencia, iniciar SSO automáticamente
      if (matchedInstitution) {
        // Multiple providers — show login page with both buttons, let user choose
        if (
          matchedInstitution.providers &&
          matchedInstitution.providers.length > 1
        ) {
          console.log(
            "Multiple SSO providers for:",
            matchedInstitution.name,
            "- showing buttons"
          );
          return;
        }

        console.log("Auto-login SSO detectado para:", matchedInstitution.name);

        // Si el usuario ya inició sesión con SSO exitosamente antes en este dominio,
        // es muy probable que tenga sesión activa en Microsoft → auto-redirect directo
        const activeSession = localStorage.getItem("sso_session_active");
        if (activeSession === hostname) {
          console.log(
            "Sesión SSO previa detectada, auto-login para:",
            matchedInstitution.name
          );
          localStorage.setItem("sso_auto_login_attempted", hostname);

          // Single provider — auto-redirect
          const provider = matchedInstitution.providers
            ? matchedInstitution.providers[0]
            : "azure";
          await this.initiateSSOLogin(matchedInstitution.id, provider);
        }
      }
    },
    async showSSOInstitutionModal(provider) {
      // Marcar que el usuario interactuó manualmente con SSO
      this.auto_sso_attempted = true;
      localStorage.setItem(
        "sso_auto_login_attempted",
        window.location.hostname
      );

      // Load institutions if not already loaded
      if (this.sso_institutions.length === 0) {
        await this.loadSSOInstitutions();
      }

      // Filter institutions that support the requested provider
      const filteredInstitutions = this.sso_institutions.filter(
        (inst) => inst.providers && inst.providers.includes(provider)
      );

      if (filteredInstitutions.length === 0) {
        this.$swal.fire({
          type: "info",
          title: this.$t("reusable.login.noSSOInstitutionsAvailable"),
          text: this.$t("reusable.login.useTraditionalSignIn"),
        });
        return;
      }

      // Extract subdomain from URL hostname
      const hostname = window.location.hostname;
      let urlSubdomain = null;

      // Extract subdomain from patterns like "ipss-dev.klearning.cl" or "ipss.klearning.cl"
      if (hostname.includes("klearning.cl") && hostname !== "klearning.cl") {
        const parts = hostname.split(".");
        if (parts.length >= 3) {
          // Get the first part and remove "-dev" suffix if present
          urlSubdomain = parts[0].replace(/-dev$/, "");
        }
      }

      // Try to match institution by URL subdomain first
      let matchedInstitution = null;
      if (urlSubdomain) {
        matchedInstitution = filteredInstitutions.find((inst) => {
          if (!inst.subdomain) return false;
          return urlSubdomain.toLowerCase() === inst.subdomain.toLowerCase();
        });

        // If matched by URL subdomain, automatically initiate SSO
        if (matchedInstitution) {
          await this.initiateSSOLogin(matchedInstitution.id, provider);
          return;
        }
      }

      // Extract domain from email if provided (fallback)
      let preselectedInstitution = null;
      if (this.user_form.email && this.user_form.email.includes("@")) {
        const emailDomain = this.user_form.email.split("@")[1].toLowerCase();

        // Find institution with matching domain
        const emailMatchedInstitution = filteredInstitutions.find((inst) => {
          if (!inst.domain) return false;

          const institutionDomain = inst.domain.toLowerCase();
          // Check if email domain matches or ends with institution domain
          return (
            emailDomain === institutionDomain ||
            emailDomain.endsWith("." + institutionDomain)
          );
        });

        if (emailMatchedInstitution) {
          preselectedInstitution = emailMatchedInstitution.id;
        }
      }

      // Create options for SweetAlert2 select
      const inputOptions = {};
      filteredInstitutions.forEach((inst) => {
        inputOptions[inst.id] = inst.name;
      });

      const providerLogo =
        provider === "google" ? this.google_logo : this.microsoft_logo;
      const providerName = provider === "google" ? "Google" : "Microsoft";

      const result = await this.$swal.fire({
        title: this.$t("reusable.login.microsoftSignIn"),
        html: `
          <div style="text-align: center; margin: 1rem 0;">
            <p style="color: #6c757d; font-size: 15px; margin: 1rem 0;">
              ${this.$t("reusable.login.institutionDetectedAutomatically")}
            </p>
          </div>
        `,
        input: "select",
        inputOptions: inputOptions,
        inputValue: preselectedInstitution || "",
        inputPlaceholder: this.$t("reusable.login.selectInstitution"),
        inputAttributes: {
          style:
            "border: 2px solid #dee2e6; border-radius: 8px; padding: 14px 18px; font-size: 16px; color: #2c3e50; background-color: #f8f9fa; font-weight: 500; box-shadow: 0 2px 4px rgba(0,0,0,0.05);",
        },
        showCancelButton: true,
        confirmButtonText: `<img src="${providerLogo}" style="width: 18px; height: 18px; margin-right: 8px; vertical-align: middle;"/> ${this.$t(
          "reusable.login.continueWithMicrosoft"
        ).replace("Microsoft", providerName)}`,
        cancelButtonText: this.$t("reusable.common.cancel"),
        width: "450px",
        padding: "2rem",
        background: "#ffffff",
        customClass: {
          popup: "azure-sso-popup-v3",
          title: "azure-sso-title-v3",
          htmlContainer: "azure-sso-content-v3",
          confirmButton: "azure-sso-confirm-btn-v3",
          cancelButton: "azure-sso-cancel-btn-v3",
          input: "azure-sso-select-v3",
        },
        buttonsStyling: false,
        inputValidator: (value) => {
          if (!value) {
            return this.$t("reusable.login.pleaseSelectInstitution");
          }
        },
      });

      // When user confirms with a selection, result.value contains the selected value
      // When user cancels or dismisses, result.dismiss is set
      if (result.value) {
        await this.initiateSSOLogin(parseInt(result.value), provider);
      }
    },
    async initiateSSOLogin(institutionId, provider) {
      try {
        //TODO: Se oculta seccion de Idioma hasta que este finalizado
        // Forzar idioma a español al iniciar SSO login
        // this.$i18n.locale = "es";
        // localStorage.setItem("user_language", "es");

        this.$swal.fire({
          title: this.$t("reusable.login.redirectingToMicrosoft"),
          text: this.$t("reusable.login.pleaseWait"),
          allowOutsideClick: false,
          showConfirmButton: false,
          showCloseButton: true,
          onBeforeOpen: () => {
            this.$swal.showLoading();
          },
        });

        // Preserve redirect destination across SSO flow (lost during backend OAuth roundtrip)
        const redirectPath = this.$route.query.redirect;
        if (redirectPath) {
          localStorage.setItem("sso_post_login_redirect", redirectPath);
        }

        await SSOService.initiateLogin(institutionId, provider);
      } catch (error) {
        console.error("Error initiating SSO login:", error);
        this.$swal.fire({
          type: "error",
          title: this.$t("reusable.login.errorSigningIn"),
          text:
            error.response?.data?.error ||
            this.$t("reusable.login.errorSigningInWithMicrosoft"),
        });
      }
    },
    redirectToInstitution(institution) {
      // Preservar el destino post-login (?redirect=...) al saltar al subdominio:
      // sin sesión, localStorage no cruza de origin, así que va por la query.
      const redirect =
        typeof this.$route.query.redirect === "string"
          ? this.$route.query.redirect
          : null;
      const target = buildSubdomainUrl(institution.subdomain, { redirect });
      if (!target) return;
      window.location.href = target;
    },
    async checkSSOCallback() {
      console.log("checkSSOCallback called");
      const authData = SSOService.extractTokensFromUrl();
      // console.log("authData:", authData);

      if (!authData) {
        console.log("No SSO callback detected");
        return; // Not an SSO callback
      }

      if (authData.success) {
        console.log("SSO login successful, dispatching azureCallback action");

        // Marcar que este dominio tuvo un login SSO exitoso para habilitar auto-redirect futuro
        localStorage.setItem("sso_session_active", window.location.hostname);

        // Clear URL parameters first
        SSOService.clearUrlParams();

        // SEC-3 fase 2: los tokens ya no vienen en la URL — el backend dejó la
        // cookie HttpOnly en el redirect. azureCallback (provider-agnostic)
        // establece la sesión vía silent refresh (restoreSession).
        this.$store.dispatch("azureCallback").catch(() => {
          localStorage.removeItem("sso_session_active");
          this.$swal.fire({
            type: "error",
            title: this.$t("reusable.login.errorSigningIn"),
            text: this.$t("reusable.login.errorDuringMicrosoftAuthentication"),
          });
        });
      } else {
        // Login SSO fallido: limpiar flag de sesión activa para no auto-redirigir en el futuro
        localStorage.removeItem("sso_session_active");

        // Handle error
        SSOService.clearUrlParams();

        this.$swal.fire({
          type: "error",
          title: this.$t("reusable.login.errorSigningIn"),
          text:
            authData.error ||
            this.$t("reusable.login.errorDuringMicrosoftAuthentication"),
          footer: `<small style="color:#6c757d;">${this.$t(
            "reusable.login.ssoErrorHint"
          )}</small>`,
        });
      }
    },
  },
  mounted() {
    // SEC-3 fase 2: si se llega desde un callback SSO exitoso, NO cerrar la
    // sesión previa — logOut blacklistea TODOS los refresh del usuario en el
    // backend y revocaría la cookie recién emitida por el redirect SSO.
    const boot_params = new URLSearchParams(window.location.search);
    const sso_success =
      (boot_params.get("sso_login") || boot_params.get("azure_login")) ===
      "success";
    if (this.isLoged && !sso_success) this.$store.dispatch("logOut");

    // Check if returning from SSO (Azure or Google)
    this.checkSSOCallback();

    // Verificar si ya se intentó auto-login en esta sesión
    const hostname = window.location.hostname;
    const lastAttempt = localStorage.getItem("sso_auto_login_attempted");
    const urlParams = new URLSearchParams(window.location.search);
    const ssoCallback =
      urlParams.get("sso_login") || urlParams.get("azure_login");

    // Si viene de un callback SSO (éxito o error), no intentar auto-login
    if (ssoCallback) {
      this.auto_sso_attempted = true;
      // Limpiar el flag de localStorage para permitir futuros intentos
      if (ssoCallback === "error") {
        localStorage.removeItem("sso_auto_login_attempted");
      }
    }

    // Si el usuario ya hizo un intento manual (canceló el modal o declinó SSO), no hacer auto-login
    if (lastAttempt === hostname) {
      this.auto_sso_attempted = true;
    }

    // Preload SSO institutions in background (y posiblemente auto-login)
    this.loadSSOInstitutions();
    // Cargar instituciones del selector público del login
    this.loadLoginInstitutions();
  },
};
</script>

<style>
/* Estilos específicos para SweetAlert - Solo afectan elementos con clases azure-sso-*-v3 dentro de .swal2-container */
.swal2-container .swal2-popup.azure-sso-popup-v3 {
  border-radius: 12px !important;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2) !important;
}

.swal2-container .swal2-title.azure-sso-title-v3 {
  color: #2c3e50 !important;
  font-size: 24px !important;
  font-weight: 600 !important;
  margin-bottom: 0 !important;
}

.swal2-container .swal2-html-container.azure-sso-content-v3 {
  margin: 0 !important;
}

.swal2-container .swal2-popup .swal2-select.azure-sso-select-v3,
.swal2-container select.swal2-select.azure-sso-select-v3 {
  border: 2px solid #dee2e6 !important;
  border-radius: 8px !important;
  padding: 14px 18px !important;
  font-size: 16px !important;
  color: #2c3e50 !important;
  transition: all 0.3s ease !important;
  background-color: #f8f9fa !important;
  background-image: none !important;
  cursor: pointer !important;
  font-weight: 500 !important;
  width: 100% !important;
  max-width: 100% !important;
  box-sizing: border-box !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05) !important;
  height: auto !important;
  line-height: 1.5 !important;
}

.swal2-container .swal2-select.azure-sso-select-v3:hover {
  border-color: #007bff !important;
  background-color: #ffffff !important;
  box-shadow: 0 3px 8px rgba(0, 123, 255, 0.15) !important;
  transform: translateY(-1px) !important;
}

.swal2-container .swal2-select.azure-sso-select-v3:focus {
  border-color: #007bff !important;
  outline: 0 !important;
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.2),
    0 4px 12px rgba(0, 123, 255, 0.15) !important;
  background-color: #ffffff !important;
  transform: translateY(-2px) !important;
}

.swal2-container .swal2-confirm.azure-sso-confirm-btn-v3 {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  background-color: #007bff !important;
  color: white !important;
  border: 1px solid #007bff !important;
  border-radius: 4px !important;
  padding: 12px 24px !important;
  font-size: 16px !important;
  font-weight: 500 !important;
  cursor: pointer !important;
  transition: all 0.3s ease !important;
  box-shadow: none !important;
}

.swal2-container .swal2-confirm.azure-sso-confirm-btn-v3:hover {
  background-color: #0069d9 !important;
  border-color: #0062cc !important;
  transform: translateY(-2px) !important;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1) !important;
}

.swal2-container .swal2-confirm.azure-sso-confirm-btn-v3:active,
.swal2-container .swal2-confirm.azure-sso-confirm-btn-v3:focus {
  background-color: #0062cc !important;
  border-color: #005cbf !important;
  transform: translateY(0) !important;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.5) !important;
}

.swal2-container .swal2-cancel.azure-sso-cancel-btn-v3 {
  background-color: #f8f9fa !important;
  color: #6c757d !important;
  border: 1px solid #6c757d !important;
  border-radius: 4px !important;
  padding: 12px 24px !important;
  font-size: 16px !important;
  font-weight: 500 !important;
  cursor: pointer !important;
  transition: all 0.3s ease !important;
  margin-left: 10px !important;
  box-shadow: none !important;
}

.swal2-container .swal2-cancel.azure-sso-cancel-btn-v3:hover {
  background-color: #e2e6ea !important;
  border-color: #545b62 !important;
  color: #545b62 !important;
  transform: translateY(-2px) !important;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1) !important;
}

.swal2-container .swal2-cancel.azure-sso-cancel-btn-v3:active,
.swal2-container .swal2-cancel.azure-sso-cancel-btn-v3:focus {
  background-color: #dae0e5 !important;
  border-color: #4e555b !important;
  transform: translateY(0) !important;
  box-shadow: 0 0 0 0.2rem rgba(108, 117, 125, 0.5) !important;
}
</style>

<style scoped>
.kl-login-page {
  /* Migrado al sistema global de tokens (utils/styles_kl.css). */
  --kl-blue: var(--kl-color-primary);
  --kl-blue-dark: var(--kl-color-primary-deep);
  --kl-teal: var(--kl-color-secondary);
  --kl-ink: var(--kl-text-heading);
  --kl-text: var(--kl-text-body);
  --kl-border: var(--kl-border-soft);

  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 20px;
  background: linear-gradient(
    135deg,
    var(--kl-blue-dark) 0%,
    var(--kl-blue) 55%,
    var(--kl-teal) 130%
  );
}
.login-box {
  margin-left: auto;
  margin-right: auto;
  background-color: var(--kl-surface);
  padding: 52px 52px;
  border-radius: 24px;
  box-shadow: 0 30px 70px rgba(0, 0, 0, 0.28);
  max-width: 560px;
  width: 100%;
}
.login-box__header {
  text-align: center;
  margin-bottom: 30px;
}
.login-box__logo {
  height: 54px;
  width: auto;
  margin-bottom: 16px;
}
.login-box__title {
  font-weight: var(--kl-fw-bold);
  font-size: var(--kl-fs-title-md);
  margin: 0;
  color: var(--kl-ink);
  letter-spacing: -0.01em;
}

/* Inputs */
.login-box ::v-deep label {
  font-weight: var(--kl-fw-semibold);
  color: var(--kl-text);
  margin-bottom: 6px;
}
.login-box ::v-deep .form-control {
  height: 48px;
  border: 1.5px solid rgba(0, 110, 200, 0.35);
  border-radius: var(--kl-radius-lg);
  background-color: #f5f9fd;
  color: var(--kl-ink);
  transition: border-color 0.25s ease, box-shadow 0.25s ease,
    background-color 0.25s ease;
}
.login-box ::v-deep .form-control:focus {
  border-color: var(--kl-blue);
  background-color: #ffffff;
  box-shadow: 0 0 0 3px rgba(0, 110, 200, 0.15);
}

/* Campo de contraseña + botón mostrar/ocultar uniformes */
.login-password {
  align-items: stretch;
}
.login-password ::v-deep .form-control {
  flex: 1;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  border-right: none;
}
.login-password ::v-deep .input-group-append {
  margin-left: 0;
}
.login-password ::v-deep .btn {
  height: 48px;
  width: 50px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1.5px solid rgba(0, 110, 200, 0.35);
  border-left: none;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
  border-top-right-radius: var(--kl-radius-lg);
  border-bottom-right-radius: var(--kl-radius-lg);
  background-color: #f5f9fd;
  color: #8a97a5;
  box-shadow: none;
}
.login-password ::v-deep .btn svg {
  color: #8a97a5;
  fill: currentColor;
}
.login-password ::v-deep .btn:hover {
  background-color: #eaf3fb;
  color: var(--kl-blue);
  transform: none;
}
.login-password ::v-deep .btn:hover svg {
  color: var(--kl-blue);
}

/* Botón principal */
.login-box ::v-deep .btn-primary {
  width: 100%;
  height: 52px;
  border: none;
  border-radius: 14px;
  font-weight: var(--kl-fw-bold);
  font-size: var(--kl-fs-small);
  letter-spacing: 0.06em;
  text-transform: uppercase;
  background: linear-gradient(135deg, var(--kl-blue) 0%, var(--kl-teal) 100%);
  box-shadow: 0 10px 24px rgba(0, 110, 200, 0.3);
  transition: transform 0.25s ease, box-shadow 0.25s ease, filter 0.25s ease;
}
.login-box ::v-deep .btn-primary:hover {
  transform: translateY(-2px);
  filter: brightness(1.05);
  box-shadow: 0 14px 30px rgba(0, 110, 200, 0.38);
}

.forgot-password {
  color: var(--kl-blue);
  margin: 4px 0 22px;
  font-size: var(--kl-fs-body);
  font-weight: var(--kl-fw-semibold);
  text-align: right;
  cursor: pointer;
  transition: color 0.25s ease;
}
.forgot-password:hover {
  color: var(--kl-blue-dark);
  text-decoration: underline;
}

/* SSO Styles */
.sso-divider {
  display: flex;
  align-items: center;
  text-align: center;
  margin: 1.5rem 0;
}

.sso-divider::before,
.sso-divider::after {
  content: "";
  flex: 1;
  border-bottom: 1px solid #dee2e6;
}

.sso-divider span {
  padding: 0 10px;
  color: var(--kl-text-muted);
  font-weight: var(--kl-fw-medium);
}

.sso-section {
  margin-top: 1rem;
}

.sso-button {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: var(--kl-fw-medium);
  transition: all 0.3s ease;
}

.login-box ::v-deep .sso-button.btn-outline-primary {
  height: 48px;
  border-radius: var(--kl-radius-lg);
  border: 1.5px solid rgba(0, 110, 200, 0.4);
  color: var(--kl-blue-dark);
  font-weight: var(--kl-fw-semibold);
}
.login-box ::v-deep .sso-button.btn-outline-primary:hover {
  background-color: #f0f7fd;
  border-color: var(--kl-blue);
  color: var(--kl-blue-dark);
}

.sso-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.microsoft-icon {
  width: 20px;
  height: 20px;
  cursor: pointer;
  background-color: #fff;
  border-radius: 4px;
  padding: 3px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
}

.sso-button .microsoft-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
}
.container-login {
  position: relative;
  margin: auto;
  width: 100%;
}

.institutions-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 10px;
  margin-bottom: 0.5rem;
}

.institution-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 5px 5px;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  cursor: pointer;
  text-align: center;
  transition: all 0.2s ease;
  background-color: #f8f9fa;
  color: #2c3e50;
  max-width: 160px;
  max-height: 70px !important;
  min-height: 70px !important;
}

.institution-card:hover {
  border-color: var(--kl-color-primary);
  background-color: #eaf3fb;
  transform: translateY(-2px);
  box-shadow: 0 3px 8px rgba(0, 110, 200, 0.18);
  color: var(--kl-color-primary);
}

.institution-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  max-width: 160px !important;
  max-height: 60px !important;
}

.institution-logo {
  width: 100% !important;
  height: 100% !important;
  object-fit: contain;
  border-radius: 4px;
}

.institution-name {
  font-size: var(--kl-fs-caption);
  font-weight: var(--kl-fw-medium);
  line-height: 1.2;
  word-break: break-word;
}
</style>
